import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	getNotifications,
	markNotificationAsRead,
	resolveNotification,
} from '@/api/services/notifications'
import type { NotificationListOutput } from '@/types/notification'

const QUERY_KEY = ['notifications', 'list']

export function useNotifications() {
	return useQuery<NotificationListOutput, Error>({
		queryKey: QUERY_KEY,
		queryFn: getNotifications,
		staleTime: 60 * 1000,
	})
}

export function useMarkNotificationAsRead() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: markNotificationAsRead,
		onMutate: async (id: string) => {
			await queryClient.cancelQueries({ queryKey: QUERY_KEY })
			const previous = queryClient.getQueriesData<NotificationListOutput>({
				queryKey: QUERY_KEY,
			})

			queryClient.setQueriesData<NotificationListOutput>(
				{ queryKey: QUERY_KEY },
				(current) => {
					if (!current) return current
					return {
						...current,
						items: current.items.map((item) =>
							item.id === id
								? { ...item, readAt: new Date().toISOString() }
								: item,
						),
					}
				},
			)

			return { previous }
		},
		onError: (_error, _id, context) => {
			context?.previous.forEach(([queryKey, data]) => {
				queryClient.setQueryData(queryKey, data)
			})
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
		},
	})
}

export function useResolveNotification() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: resolveNotification,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
		},
	})
}
